import {useFormContext} from 'react-hook-form'
import {useI18n} from 'store/Store'
import {FormColumn, FormContainer} from '../Form/Forms'
import FormTextInput from 'components/Control/Form/FormTextInput'
import FormSelect from 'components/Control/Form/FormSelect'
import {yesNoOptions} from 'constants/users'
import FormAutocomplete from 'components/Control/Form/FormAutocomplete'
import {formatItemName, formatUser} from 'services/utils/data'
import {types} from 'constants/dances'
import FormDatePicker from 'components/Control/Form/FormDatePicker'
import TimeTable from './TimeTable'
import FormChipAutocomplete from 'components/Control/Form/FormChipAutocomplete'
import FormAddressInput from 'components/Control/Form/FormAddressInput'
import {getLocale} from 'localization/i18n'
import LearnedDances from './LearnedDances'
import FormImageUploader from 'components/Control/Form/FormImageUploader'
import OccurrenceFields from './OccurrenceFields'
import {FormRow} from '../Form/FormRow'

const SessionForm = ({
    isNew,
    session,
    guidesResults,
    searchGuideValue,
    setSearchGuideValue,
    countriesResults,
    searchCountryValue,
    setSearchCountryValue,
    citiesResults,
    searchCityValue,
    setSearchCityValue,
    handleSetValue,
    imageUrl,
    uploadImage,
    handleMoveDance,
    handleRemoveDance,
    learnedDancesValues,
}) => {
    const {i18n} = useI18n()
    const formContext = useFormContext()
    const locale = getLocale()

    const {
        control,
        formState: {errors},
    } = formContext

    const occurrenceValue = formContext.watch('occurrences')
    const addressInputName =
        locale === 'he' ? 'formattedAddressHeb' : 'formattedAddressEng'

    return (
        <FormContainer>
            <FormRow>
                <FormColumn>
                    <FormTextInput
                        readOnly
                        hidden={isNew}
                        name="_id"
                        label={i18n.t(`sessions._id`)}
                        control={control}
                        error={errors._id?.message}
                    />

                    <FormChipAutocomplete
                        name="guides"
                        label={i18n.t(`sessions.guides`)}
                        options={guidesResults?.data}
                        control={control}
                        error={errors.guides?.message}
                        getOptionLabel={formatUser}
                        inputValue={searchGuideValue}
                        setInputValue={setSearchGuideValue}
                    />

                    <FormTextInput
                        name="description"
                        label={i18n.t(`sessions.description`)}
                        control={control}
                        error={errors.description?.message}
                    />

                    <FormTextInput
                        name="price"
                        label={i18n.t(`sessions.price`)}
                        control={control}
                        error={errors.price?.message}
                    />

                    <FormSelect
                        itemsName="dances"
                        optionsName="types"
                        name="type"
                        label={i18n.t(`sessions.type`)}
                        options={types}
                        control={control}
                        error={errors.type?.message}
                    />

                    <FormRow>
                        <FormAutocomplete
                            name="country"
                            label={i18n.t(`users.country`)}
                            control={control}
                            error={errors.country?.message}
                            options={countriesResults?.data}
                            getOptionLabel={formatItemName}
                            inputValue={searchCountryValue}
                            onChangeInputValue={setSearchCountryValue}
                        />

                        <FormAutocomplete
                            name="city"
                            label={i18n.t(`users.city`)}
                            control={control}
                            error={errors.city?.message}
                            options={citiesResults?.data}
                            getOptionLabel={formatItemName}
                            inputValue={searchCityValue}
                            onChangeInputValue={setSearchCityValue}
                        />
                    </FormRow>

                    <FormRow>
                        <FormTextInput
                            name="placeName"
                            label={i18n.t(`sessions.placeName`)}
                            control={control}
                            error={errors.placeName?.message}
                        />

                        <FormAddressInput
                            name={`googleLocation.${addressInputName}`}
                            label={i18n.t(`sessions.address`)}
                            control={control}
                            setGoogleLocation={handleSetValue}
                            error={errors.address?.message}
                        />
                    </FormRow>
                </FormColumn>

                <FormColumn>
                    <FormRow>
                        <FormSelect
                            itemsName="users"
                            optionsName="yesNo"
                            name="isActive"
                            label={i18n.t(`sessions.isActive`)}
                            options={yesNoOptions}
                            control={control}
                            error={errors.isActive?.message}
                        />
                        <FormSelect
                            itemsName="users"
                            optionsName="yesNo"
                            name="highlight"
                            label={i18n.t(`sessions.highlight`)}
                            options={yesNoOptions}
                            control={control}
                            error={errors.highlight?.message}
                        />
                    </FormRow>

                    <FormSelect
                        itemsName="users"
                        optionsName="yesNo"
                        name="isWeekly"
                        label={i18n.t(`sessions.isWeekly`)}
                        options={yesNoOptions}
                        control={control}
                        error={errors.isWeekly?.message}
                    />

                    <FormSelect
                        itemsName="users"
                        optionsName="yesNo"
                        name="airConditioner"
                        label={i18n.t(`sessions.airConditioner`)}
                        options={yesNoOptions}
                        control={control}
                        error={errors.airConditioner?.message}
                    />

                    <OccurrenceFields
                        occurrenceValue={occurrenceValue}
                        control={control}
                        errors={errors}
                    />
                    <FormDatePicker
                        name="startDateBeginners"
                        label={i18n.t(`sessions.startDateBeginners`)}
                        control={control}
                        error={errors.startDateBeginners?.message}
                    />
                </FormColumn>
            </FormRow>

            <TimeTable data={session?.timeTable} />

            <FormImageUploader
                label={i18n.t('sessions.picture')}
                name="imageUrl"
                control={control}
                onUpload={uploadImage}
                url={imageUrl}
                showImage
            />

            <LearnedDances
                learnedDances={learnedDancesValues || session?.learnedDances}
                handleMoveDance={handleMoveDance}
                handleRemoveDance={handleRemoveDance}
            />
        </FormContainer>
    )
}

export default SessionForm
